import { AuthService } from '@/services/authService';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
  RadioGroup,
  RadioGroupItem,
  Separator,
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@opaz/ui-components';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useState, memo } from 'react';

import { ChevronLeftFilled, LogoutFilled, Menu, PersonOutlineFilled } from '@/assets/icons';
import logoSvgArabic from '@/assets/logo-color-arabic.svg';
import logoSvg from '@/assets/logo-color.svg';
import { cn, getAvatarInitials, getLocalizedName } from '@/lib/utils';
import { useAuthStore } from '@/store/auth';
import { Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '@/hooks/useAnalytics';
import AddProcessDialog from './add-process-dialog';
import { HeaderLink } from './header-link';
import NavBarProfileItem from './nav-bar-profile-item';

const Logo = memo(({ onClick }: { onClick?: () => void }) => {
  const { i18n } = useTranslation();
  return (
    <Link to="/" className="shrink-0" preload={false} onClick={onClick}>
      <img src={i18n.language === 'ar' ? logoSvgArabic : logoSvg} alt="OPAZ Logo" className="h-[36px] sm:h-[48px]" />
    </Link>
  );
});
Logo.displayName = 'Logo';

export function NavBar() {
  const { t, i18n } = useTranslation();
  const { activeProfile, userProfile: user } = useAuthStore();
  const analytics = useAnalytics();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLanguageSelectOpen, setIsLanguageSelectOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = !AuthService.isTokenExpired();
  const showDebugTools = new URLSearchParams(location.searchStr).has('debugTools');

  const profiles = user?.profiles;

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;

    if (isMobileMenuOpen) {
      html.style.overflow = 'hidden';
      body.style.overflow = 'hidden';
    } else {
      html.style.overflow = '';
      body.style.overflow = '';
    }

    return () => {
      html.style.overflow = '';
      body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setIsLanguageSelectOpen(false);

    // Track language toggle
    if (analytics) {
      analytics.trackLanguageToggle(lng);
    }
  };

  const handleNavigationClick = useCallback(
    (itemName: string) => {
      if (analytics) {
        analytics.trackNavigationClick(`Main Nav: ${itemName}`);
      }
    },
    [analytics],
  );

  const handleLoginClick = () => {
    if (analytics) {
      analytics.trackLoginClick();
    }

    const currentPath = location.pathname + location.searchStr;
    navigate({
      to: '/login',
      search: { redirect: currentPath },
    });
  };

  const handleSignupClick = () => {
    if (analytics) {
      analytics.trackNavigationClick('Create Account');
    }

    navigate({
      to: '/signup',
      search: {
        txId: undefined,
        mpkId: undefined,
        redirect: location.pathname + location.searchStr,
      },
    });
  };

  const handleProfileClick = () => {
    if (analytics) {
      analytics.trackNavigationClick('Profile');
    }

    navigate({
      to: '/profile',
    });
  };

  const handleLogoutClick = () => {
    if (analytics) {
      analytics.trackNavigationClick('Logout');
    }

    navigate({ to: '/logout' });
  };

  const renderProfiles = useCallback(() => {
    if (!profiles) return null;

    return profiles
      .filter((profile) => profile.id !== activeProfile.id)
      .map((profile) => <NavBarProfileItem key={profile.id} profile={profile} />);
  }, [profiles, activeProfile]);

  const renderActiveProfileAvatar = useCallback(() => {
    return (
      <Avatar status="online">
        <AvatarImage src="/avatars/01.png" />
        <AvatarFallback>
          {getAvatarInitials(getLocalizedName(activeProfile?.nameEn, activeProfile?.nameAr, i18n.language))}
        </AvatarFallback>
      </Avatar>
    );
  }, [activeProfile?.nameAr, activeProfile?.nameEn, i18n.language]);

  return (
    <div className="bg-white">
      <nav className="flex h-[72px] border-b-2 items-center px-2 sm:px-3 overflow-hidden">
        <div className="flex flex-1 max-w-[1200px] mx-auto items-center justify-between h-[inherit] min-w-0">
          {/* Left section with logo and main navigation */}
          <div className="flex items-center gap-1 sm:gap-2 h-[inherit] min-w-0 flex-shrink-0">
            {/* Logo */}
            <Logo onClick={() => handleNavigationClick('Logo')} />

            {/* DESKTOP: Main navigation */}
            <div className="hidden xl:flex items-center gap-2 h-[inherit] ml-4 flex-shrink-0">
              <HeaderLink to="/" onClick={() => handleNavigationClick('Home')}>
                {t('nav.items.home')}
              </HeaderLink>
              <HeaderLink to="/about-us" onClick={() => handleNavigationClick('About Us')}>
                {t('nav.items.aboutUs')}
              </HeaderLink>
              <HeaderLink to="/zones" onClick={() => handleNavigationClick('Zones')}>
                {t('nav.items.zones')}
              </HeaderLink>
              <HeaderLink to="/services" onClick={() => handleNavigationClick('Services')}>
                {t('nav.items.services')}
              </HeaderLink>
              {isAuthenticated && (
                <HeaderLink to="/dashboard" onClick={() => handleNavigationClick('Dashboard')}>
                  {t('nav.items.dashboard')}
                </HeaderLink>
              )}
            </div>
          </div>

          {/* Right section with search, language, notifications and profile */}
          <div className="flex items-center gap-2 sm:gap-3 md:gap-4 flex-shrink-0">
            <div className="flex items-center gap-2 sm:gap-3">
              {isAuthenticated && showDebugTools && (
                <>
                  <AddProcessDialog />
                </>
              )}

              {/* Language selector */}
              <DropdownMenu open={isLanguageSelectOpen} onOpenChange={setIsLanguageSelectOpen} modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="border-gray-200 h-9 w-9 sm:h-10 sm:w-10"
                    onClick={() => analytics?.trackNavigationClick('Language Selector')}
                  >
                    <Globe className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent className="w-[200px] px-4 py-6 z-[999]" forceMount>
                  <RadioGroup size="small" onValueChange={changeLanguage} value={i18n.language} dir={i18n.dir()}>
                    {i18n.language !== 'en' && <RadioGroupItem value="en">English</RadioGroupItem>}
                    {i18n.language !== 'ar' && <RadioGroupItem value="ar">العربية</RadioGroupItem>}
                  </RadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* DESKTOP: Separator, hidden on mobile */}
            <Separator orientation="vertical" className="h-6 hidden xl:block" />

            {isAuthenticated ? (
              // 👉 Authenticated: Show Profile Dropdown
              <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen} modal={false} dir={i18n.dir()}>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-1 sm:gap-2 text-muted-foreground relative flex-shrink-0"
                    onClick={() => analytics?.trackNavigationClick('Profile Menu')}
                  >
                    {renderActiveProfileAvatar()}
                    {/* DESKTOP: Show name on desktop, hide on mobile */}
                    <span className="text-base font-medium hidden xl:inline truncate max-w-[120px]">
                      {getLocalizedName(activeProfile?.nameEn, activeProfile?.nameAr, i18n.language)}
                    </span>
                    <ChevronLeftFilled
                      className={cn('rotate-[270deg] transition-all hidden xl:inline flex-shrink-0', {
                        'rotate-90': isMenuOpen,
                      })}
                    ></ChevronLeftFilled>
                  </button>
                </DropdownMenuTrigger>

                <DropdownMenuContent className="w-[288px] px-3 py-6 rounded-lg z-[1000]" align="end" forceMount>
                  {/* Top section */}
                  <span className="text-base mb-4 block text-foreground">{t('nav.user.switchAccount')}</span>

                  <div className="max-h-[340px] overflow-y-auto">
                    <div className="flex flex-row items-center gap-3 px-4 py-2 rounded-sm bg-states-base-bg-control border-l-2 border-brand-medium">
                      {renderActiveProfileAvatar()}
                      <div className="min-w-0">
                        <p className="text-base text-foreground truncate">
                          {getLocalizedName(activeProfile?.nameEn, activeProfile?.nameAr, i18n.language)}
                        </p>
                        <p className="text-sm text-tertiary truncate">{activeProfile.identifier}</p>
                      </div>
                    </div>

                    <div>{renderProfiles()}</div>
                  </div>

                  <hr className="border-medium my-4" />

                  <DropdownMenuGroup className="p-0">
                    <DropdownMenuItem
                      className="gap-3 p-3 [&_svg]:size-6 text-foreground font-medium"
                      onClick={handleProfileClick}
                    >
                      <PersonOutlineFilled className="text-muted-foreground" />
                      {t('nav.user.profile')}
                    </DropdownMenuItem>

                    <DropdownMenuItem
                      className="gap-3 p-3 [&_svg]:size-6 text-foreground font-medium"
                      onClick={handleLogoutClick}
                    >
                      <LogoutFilled className="text-muted-foreground" />
                      {t('nav.user.logout')}
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              // 👉 Not Authenticated: Show Sign In / Create Account CTAs
              <div className="hidden xl:flex items-center gap-3">
                <Button onClick={handleLoginClick} variant="secondary" className="h-10 rounded-full px-6">
                  {t('nav.user.signIn')}
                </Button>

                <Button className="h-10 rounded-full px-6" onClick={handleSignupClick}>
                  {t('nav.user.createAccount')}
                </Button>
              </div>
            )}

            {/* MOBILE: Hamburger Menu Button, hidden on desktop */}
            <div className="xl:hidden flex-shrink-0">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={() => analytics?.trackNavigationClick('Mobile Menu')}>
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>

                <SheetContent>
                  <SheetHeader>
                    <SheetTitle>
                      <Logo />
                    </SheetTitle>
                  </SheetHeader>

                  <div className="flex flex-col items-center justify-center h-full gap-6 p-8">
                    {/* Mobile Navigation Links */}
                    <SheetClose asChild>
                      <HeaderLink
                        to="/"
                        onClick={() => {
                          handleNavigationClick('Mobile: Home');
                          setIsMobileMenuOpen(false);
                        }}
                      >
                        {t('nav.items.home')}
                      </HeaderLink>
                    </SheetClose>

                    <SheetClose asChild>
                      <HeaderLink
                        to="/about-us"
                        onClick={() => {
                          handleNavigationClick('Mobile: About Us');
                          setIsMobileMenuOpen(false);
                        }}
                      >
                        {t('nav.items.aboutUs')}
                      </HeaderLink>
                    </SheetClose>

                    <SheetClose asChild>
                      <HeaderLink
                        to="/zones"
                        onClick={() => {
                          handleNavigationClick('Mobile: Zones');
                          setIsMobileMenuOpen(false);
                        }}
                      >
                        {t('nav.items.zones')}
                      </HeaderLink>
                    </SheetClose>

                    <SheetClose asChild>
                      <HeaderLink
                        to="/services"
                        onClick={() => {
                          handleNavigationClick('Mobile: Services');
                          setIsMobileMenuOpen(false);
                        }}
                      >
                        {t('nav.items.services')}
                      </HeaderLink>
                    </SheetClose>

                    {isAuthenticated && (
                      <SheetClose asChild>
                        <HeaderLink
                          to="/dashboard"
                          onClick={() => {
                            handleNavigationClick('Mobile: Dashboard');
                            setIsMobileMenuOpen(false);
                          }}
                        >
                          {t('nav.items.dashboard')}
                        </HeaderLink>
                      </SheetClose>
                    )}

                    <Separator className="my-4 w-1/2" />

                    {/* Mobile Auth Buttons */}
                    {!isAuthenticated && (
                      <div className="flex flex-col items-center gap-4 w-full">
                        <Button
                          onClick={() => {
                            setIsMobileMenuOpen(false);
                            handleLoginClick();
                          }}
                          variant="secondary"
                          className="h-12 rounded-full px-6 w-full max-w-xs"
                        >
                          {t('nav.user.signIn')}
                        </Button>
                        <Button
                          className="h-12 rounded-full px-6 w-full max-w-xs"
                          onClick={() => {
                            setIsMobileMenuOpen(false);
                            handleSignupClick();
                          }}
                        >
                          {t('nav.user.createAccount')}
                        </Button>
                      </div>
                    )}
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </nav>
    </div>
  );
}
